// Tennis Dashboard data — aggregates the tennis product pool into one overview.
//
// Reads every published row from tennis_product_picks (anon-readable pool, the
// same one get_tennis_product serves) and folds it against the product registry
// so the dashboard can show ALL products with their live/awaiting status. No new
// RPC needed — one select, aggregated client-side.
//
// HONEST-EMPTY: products with no published row show as "Awaiting"; the dashboard
// never fabricates picks or metrics.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../../../supabase/browser.ts";
import { TENNIS_PRODUCTS, type ProductFamily } from "./registry.ts";
import type { TierGate } from "../../../auth/tier-gates.ts";

interface PoolRow {
  product_slug: string;
  format: string;
  tournament: string | null;
  pick_date: string | null;
  payload: Record<string, unknown> | null;
  published_at: string | null;
}

export interface TennisProductStatus {
  slug: string;
  name: string;
  family: ProductFamily;
  tier: TierGate;
  tierLabel: string;
  format: string;
  published: boolean;
  pickCount: number;
  topEdge: number | null;
  combinedOdds: number | null;
  pickDate: string | null;
  publishedAt: string | null;
}

export interface TennisDashboardState {
  loading: boolean;
  error: string | null;
  tournament: string | null;
  latestDate: string | null;
  products: TennisProductStatus[];
  totals: { live: number; total: number; picks: number; topEdge: number | null };
  refetch: () => void;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function summarisePayload(payload: Record<string, unknown> | null): {
  pickCount: number;
  topEdge: number | null;
  combinedOdds: number | null;
} {
  if (!payload) return { pickCount: 0, topEdge: null, combinedOdds: null };
  const picks = Array.isArray(payload.picks) ? (payload.picks as Record<string, unknown>[]) : [];
  const headline = (payload.headline && typeof payload.headline === "object" ? payload.headline : {}) as Record<string, unknown>;
  let topEdge: number | null = null;
  for (const p of picks) {
    const e = num(p.edge_pct ?? p.edge);
    if (e !== null && (topEdge === null || e > topEdge)) topEdge = e;
  }
  const pickCount = picks.length || num(headline.count) || num(headline.leg_count) || 0;
  return { pickCount, topEdge, combinedOdds: num(headline.combined_odds) };
}

export function useTennisDashboard(): TennisDashboardState {
  const [rows, setRows] = useState<PoolRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const { data, error: qErr } = await (
          client.from as unknown as (t: string) => {
            select: (c: string) => {
              order: (
                c: string,
                o: { ascending: boolean },
              ) => {
                order: (
                  c: string,
                  o: { ascending: boolean },
                ) => Promise<{ data: PoolRow[] | null; error: { message: string } | null }>;
              };
            };
          }
        )("tennis_product_picks")
          .select("product_slug,format,tournament,pick_date,payload,published_at")
          .order("pick_date", { ascending: false })
          .order("published_at", { ascending: false });

        if (cancelled) return;
        if (qErr) {
          setError(qErr.message);
          setRows([]);
          setLoading(false);
          return;
        }
        setRows(data ?? []);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setRows([]);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tick]);

  return useMemo<TennisDashboardState>(() => {
    // Latest published row per slug (rows arrive newest-first).
    const latestBySlug = new Map<string, PoolRow>();
    let latestDate: string | null = null;
    let tournament: string | null = null;
    for (const r of rows ?? []) {
      if (!latestBySlug.has(r.product_slug)) latestBySlug.set(r.product_slug, r);
      if (r.pick_date && (latestDate === null || r.pick_date > latestDate)) latestDate = r.pick_date;
      if (!tournament && r.tournament) tournament = r.tournament;
    }

    let picks = 0;
    let topEdge: number | null = null;
    const products: TennisProductStatus[] = TENNIS_PRODUCTS.map((p) => {
      const row = latestBySlug.get(p.slug);
      const sum = summarisePayload(row?.payload ?? null);
      const published = !!row && sum.pickCount > 0;
      if (published) {
        picks += sum.pickCount;
        if (sum.topEdge !== null && (topEdge === null || sum.topEdge > topEdge)) topEdge = sum.topEdge;
      }
      return {
        slug: p.slug,
        name: p.name,
        family: p.family,
        tier: p.tier,
        tierLabel: p.tierLabel,
        format: row?.format ?? p.format,
        published,
        pickCount: sum.pickCount,
        topEdge: sum.topEdge,
        combinedOdds: sum.combinedOdds,
        pickDate: row?.pick_date ?? null,
        publishedAt: row?.published_at ?? null,
      };
    });

    const live = products.filter((p) => p.published).length;
    return {
      loading,
      error,
      tournament,
      latestDate,
      products,
      totals: { live, total: products.length, picks, topEdge },
      refetch,
    };
  }, [rows, loading, error, refetch]);
}
